<template>
  <div ref="module">
    <div class="tabs">
      <div
          role="button"
          class="tab"
          :class="isActiveCategoriesTable ? 'is-active' : ''"
          @click="showCategoriesTable"
      >
        Categories
        </div>
      <div
          role="button"
          class="tab"
          :class="isActiveMaterialsTable ? 'is-active' : ''"
          @click="showMaterialsTable"
      >
        Materials
      </div>
    </div>
    <div v-show="isActiveCategoriesTable" class="grid-module__filter table-menu">
      <b-field grouped class="table-menu__selection">
        <span class="label">Type</span>
        <div class="multiselect">
          <div class="selectBox">
            <b-select v-model="categoryType">
              <option :value="null">All</option>
              <option
                  v-for="type in categoryTypesOptions"
                  :value="type"
                  :key="type"
              >
                {{ type }}
              </option>
            </b-select>
          </div>
        </div>
      </b-field>
      <b-field class="table-menu-categories-btn"
               v-if="hasAccess(MANAGE_SETTINGS_MODIFY,MANAGE_SETTINGS_MODIFY, true)" grouped>
        <b-button icon-pack="fas" icon-left="plus"
                  type="is-success" @click="onAddCategoryClick">
          Add category
        </b-button>
      </b-field>
      <b-field></b-field>
    </div>
    <div v-show="isActiveMaterialsTable" class="grid-module__filter table-menu">
      <b-field grouped class="table-menu__selection">
        <span class="label">Category</span>
        <div class="multiselect">
          <div class="selectBox">
            <b-select v-model="materialsFilterCategoryId">
              <option :value="null">All</option>
              <option
                  v-for="category in allCategories"
                  :value="category.id"
                  :key="category.id"
              >
                {{ category.name }}
              </option>
            </b-select>
          </div>
        </div>
      </b-field>
      <b-field class="table-menu-categories-btn"
               v-if="hasAccess(MANAGE_SETTINGS_MODIFY,MANAGE_SETTINGS_MODIFY, true)" grouped>
        <b-button icon-pack="fas" icon-left="plus"
                  type="is-success" @click="onAddMaterialClick">
          Add material
        </b-button>
      </b-field>
      <b-field></b-field>
    </div>

    <div v-show="isActiveCategoriesTable">
      <div class="grid-module__grid">
        <b-table
            :data="categories"
            :columns="categoriesColumns"
            :loading="categoriesTableLoading"
            :default-sort="[categoriesDefaultSort, categoriesDefaultSortDirection]"
            hoverable
            sticky-header
            backend-sorting
            ref="tbl"
            class="bordered-table"
        >
          <template slot="default" slot-scope="props" class="table-template">
            <b-table-column cell-class="cell" label="Name" width="300">
              <span :title="props.row.description" class="ellipsis cell__value">
                {{ props.row.name }}
              </span>
            </b-table-column>

            <b-table-column cell-class="cell" label="Description" width="900">
              <span :title="props.row.description" class="ellipsis cell__value">
                {{ props.row.description }}
              </span>
            </b-table-column>

            <b-table-column cell-class="cell" label="Type" width="50">
              <span class="cell__value">{{ props.row.type }}</span>
            </b-table-column>

            <b-table-column cell-class="cell cell--centered" label="Order" width="10">
              <span class="cell__value" :title="props.row.order">{{ props.row.order }}</span>
            </b-table-column>

            <b-table-column width="10" :custom-key="'find-materials'">
              <span class="cell__label">Show materials</span>
              <div class="cell__value">
                <b-button
                    class="action-column-btn icon"
                    @click="onFindMaterialsClick(props.row.id, true)"
                >
                  <i class="fal fa-file-search fa-lg"></i>
                </b-button>
              </div>
            </b-table-column>

            <b-table-column :visible="hasAccess(
              MANAGE_SETTINGS_MODIFY,MANAGE_SETTINGS_MODIFY, true)"
                            width="10" :custom-key="'edit-category'">
              <span class="cell__label">Edit</span>
              <div class="cell__value">
                <b-button
                  class="action-column-btn icon"
                  @click="onEditCategoryClick(props.row, true)">
                  <i class="fas fa-pencil-alt fa-lg"></i>
                </b-button>
              </div>
            </b-table-column>

            <b-table-column :visible="hasAccess(
              MANAGE_SETTINGS_MODIFY,MANAGE_SETTINGS_MODIFY, true)"
                            width="10" :custom-key="'delete-category'">
              <span class="cell__label">Delete</span>
              <div class="cell__value">
                <b-button
                    @click="onDeleteCategoryClick(props.row, true)"
                    class="icon action-column-btn"
                >
                  <i class="far fa-trash-alt fa-lg"></i>
                </b-button>
              </div>
            </b-table-column>
          </template>
          <template slot="empty">
            <div v-if="materials.length === 0" class="columns is-centered" style="margin: 0;">
              <div class="column is-half enh-disabled has-text-centered">
                <b-icon pack="fal" icon="frown" size="is-large"></b-icon>
                <p>Nothing here</p>
              </div>
            </div>
          </template>
        </b-table>
      </div>
    </div>

    <div v-show="isActiveMaterialsTable">
      <div class="grid-module__grid">
        <b-table
            :data="materials"
            :columns="materialsColumns"
            :loading="materialsTableLoading"
            :total="materialsTotalPages"
            :per-page="materialsLimit"
            :current-page.sync="materialsCurrentPage"
            :default-sort="[materialsDefaultSort, materialsDefaultSortDirection]"
            aria-next-label="Next page"
            aria-previous-label="Previous page"
            aria-page-label="Page"
            aria-current-label="Current page"
            hoverable
            sticky-header
            paginated
            backend-pagination
            backend-sorting
            @page-change="onMaterialsPageChangeHandler"
            ref="tbl"
            class="bordered-table"
        >
          <template slot-scope="props">
            <b-table-column cell-class="cell" label="Title">
              <span class="ellipsis cell__value" :title="props.row.name">
                {{ props.row.name }}
              </span>
            </b-table-column>

            <b-table-column cell-class="cell" label="Content">
              <a v-if="props.row.videoSrc"
                 class="ellipsis cell__value" target="_blank" :href="props.row.videoSrc">
                {{ props.row.videoSrc }}
              </a>
              <span v-else class="ellipsis cell__value" :title="props.row.content">
                {{ props.row.content }}
              </span>
            </b-table-column>

            <b-table-column cell-class="cell" label="Category">
              <span class="ellipsis cell__value" :title="getCategoryName(props.row.categoryId)">
                {{ getCategoryName(props.row.categoryId) }}
              </span>
            </b-table-column>

            <b-table-column cell-class="cell cell--centered" label="Order">
              <span class="cell__value" :title="props.row.order">{{ props.row.order }}</span>
            </b-table-column>

            <b-table-column :visible="hasAccess(
              MANAGE_SETTINGS_MODIFY,MANAGE_SETTINGS_MODIFY, true)"
                            :custom-key="'materials-edit'" width="10"
                            >
              <span class="cell__label">Edit</span>
              <div class="cell__value">
                <b-button
                    @click="onEditMaterialClick(props.row, true)"
                    class="icon action-column-btn"
                >
                  <i class="fas fa-pencil-alt fa-lg"></i>
                </b-button>
              </div>
            </b-table-column>

            <b-table-column :visible="hasAccess(
              MANAGE_SETTINGS_MODIFY,MANAGE_SETTINGS_MODIFY, true)"
                            cell-class="cell" class="action-column" width="10">
              <span class="cell__label">Delete</span>
              <div class="cell__value">
                <b-button
                    @click="onDeleteMaterialClick(props.row, true)"
                    class="icon action-column-btn"
                >
                  <i class="far fa-trash-alt fa-lg"></i>
                </b-button>
              </div>
            </b-table-column>
          </template>
          <template slot="bottom-left">
            <b-field label="Per page" horizontal style="margin-right: 1rem" class="enh-per-page">
              <b-select v-model="materialsLimit" @input="loadMaterials"
                        :disabled="materials.length === 0">
                <option
                    v-for="perPageOption in materialsPerPageOptions"
                    :key="perPageOption"
                    :value="perPageOption"
                    :selected="perPageOption === materialsLimit"
                >{{ perPageOption }}
                </option>
              </b-select>
            </b-field>
          </template>
          <template slot="empty">
            <div v-if="materials.length === 0" class="columns is-centered" style="margin: 0;">
              <div class="column is-half enh-disabled has-text-centered">
                <b-icon pack="fal" icon="frown" size="is-large"></b-icon>
                <p>Nothing here</p>
              </div>
            </div>
          </template>
        </b-table>
      </div>
    </div>
  </div>
</template>

<script>

import { mapGetters, mapMutations } from 'vuex';
import {
  addCategory,
  addMaterial,
  categories,
  deleteCategory,
  deleteMaterial,
  editCategory, editMaterial, materials,
} from '@/api/materials';
import { TYPES_MATERIALS_CATEGORIES } from '@/views/TrainingAndFaqs.vue';
import SettingsMaterialsCategory from '@/components/Modal/SettingsMaterialsCategory.vue';
import SettingsMaterial from '@/components/Modal/SettingsMaterial.vue';
import * as MUTATIONS from '@/store/mutations';
import Vue from 'vue';
import { access } from '@/components/mixins/access';

const TABLE_MATERIALS = 'MATERIALS';
const TABLE_CATEGORIES = 'CATEGORIES';

export default {
  name: 'SettingsMaterials',
  mixins: [access],

  data() {
    return {
      activeTable: TABLE_CATEGORIES,
      MANAGE_SETTINGS_MODIFY: { resource: 'manage-settings', code: 'modify' },
      allCategories: [],
      categories: [],
      categoryType: null,
      categoryTypesOptions: TYPES_MATERIALS_CATEGORIES,
      categoriesColumns: [
      ],
      categoriesTableLoading: false,
      categoriesDefaultSort: 'order',
      categoriesDefaultSortDirection: '',

      addCategoryModal: null,
      editCategoryModal: null,

      addMaterialModal: null,
      editMaterialModal: null,

      materials: [],
      materialsFilterCategoryId: null,
      materialsTableLoading: false,
      materialsTotalPages: 0,
      materialsLimit: 25,
      materialsPerPageOptions: [10, 25, 50, 100],
      materialsCurrentPage: 1,
      materialsDefaultSort: 'order',
      materialsDefaultSortDirection: 'asc',
      materialsColumns: [
      ],
    };
  },

  mounted() {
    this.loadAllCategories();
    this.loadMaterials();
  },
  computed: {
    ...mapGetters(['authAuthenticator']),
    isActiveCategoriesTable() {
      return this.activeTable === TABLE_CATEGORIES;
    },
    isActiveMaterialsTable() {
      return this.activeTable === TABLE_MATERIALS;
    },
  },
  deactivated() {
    [this.addCategoryModal, this.editCategoryModal, this.addMaterialModal, this.editMaterialModal]
      .forEach((modal) => {
        if (modal) modal.cancel();
      });
  },
  watch: {
    categoryType() {
      if (this.categoryType) {
        this.categories = this.allCategories
          .filter((category) => category.type === this.categoryType);
      } else {
        this.categories = this.allCategories;
      }
    },
    materialsFilterCategoryId() {
      this.loadMaterials();
    },
  },
  methods: {
    ...mapMutations({
      startLoading: MUTATIONS.START_LOADING,
      finishLoading: MUTATIONS.FINISH_LOADING,
    }),
    getCategoryName(categoryId) {
      let categoryName = null;
      this.allCategories.forEach((category) => {
        if (category.id === categoryId) categoryName = category.name;
      });
      return categoryName;
    },
    showCategoriesTable() {
      this.activeTable = TABLE_CATEGORIES;
    },
    showMaterialsTable() {
      this.activeTable = TABLE_MATERIALS;
    },
    onFindMaterialsClick(id) {
      this.materialsFilterCategoryId = id;
      this.showMaterialsTable();
    },

    onAddCategoryClick() {
      const Component = Vue.extend(SettingsMaterialsCategory);
      const modal = new Component({
        propsData: {
          active: true,
          categoryType: this.categoryType,
        },
      });
      modal.$store = this.$store;
      modal.$mount();
      modal.$on('submit', this.addCategory);
      modal.$on('close', () => {
        modal.active = false;
        this.$refs.module.removeChild(modal.$el);
      });
      this.$refs.module.appendChild(modal.$el);
      this.addCategoryModal = modal;
    },
    onEditCategoryClick(category) {
      const Component = Vue.extend(SettingsMaterialsCategory);
      const modal = new Component({
        propsData: {
          active: true,
          category,
          isEdit: true,
        },
      });
      modal.$store = this.$store;
      modal.$mount();
      modal.$on('submit', this.editCategory);
      modal.$on('close', () => {
        modal.active = false;
        this.$refs.module.removeChild(modal.$el);
      });
      this.$refs.module.appendChild(modal.$el);
      this.editCategoryModal = modal;
    },
    onDeleteCategoryClick(data) {
      this.$buefy.dialog.confirm({
        title: 'Deleting category',
        message: 'Are you sure you want to <b>delete</b> this category? This action cannot be undone.',
        confirmText: 'Delete',
        type: 'is-danger',
        hasIcon: true,
        ariaModal: true,
        onConfirm: () => this.deleteCategory(data.id),
      });
    },

    onAddMaterialClick() {
      const Component = Vue.extend(SettingsMaterial);
      const modal = new Component({
        propsData: {
          title: 'Add material',
          active: true,
          categories: this.allCategories,
          categoryId: this.materialsFilterCategoryId,
        },
      });
      modal.$store = this.$store;
      modal.$mount();
      modal.$on('submit', this.addMaterial);
      modal.$on('close', () => {
        modal.active = false;
        this.$refs.module.removeChild(modal.$el);
      });
      this.$refs.module.appendChild(modal.$el);
      this.addMaterialModal = modal;
    },
    onEditMaterialClick(material) {
      let materialCategoryType = null;
      this.allCategories.forEach((item) => {
        if (item.id === material.categoryId) materialCategoryType = item.type;
      });
      const Component = Vue.extend(SettingsMaterial);
      const modal = new Component({
        propsData: {
          title: 'Edit material',
          active: true,
          categories: this.allCategories.filter((item) => item.type === materialCategoryType),
          material,
        },
      });
      modal.$store = this.$store;
      modal.$mount();
      modal.$on('submit', this.editMaterial);
      modal.$on('close', () => {
        modal.active = false;
        this.$refs.module.removeChild(modal.$el);
      });
      this.$refs.module.appendChild(modal.$el);
      this.editMaterialModal = modal;
    },
    onDeleteMaterialClick(data) {
      this.$buefy.dialog.confirm({
        title: 'Deleting material',
        message: 'Are you sure you want to <b>delete</b> this material? This action cannot be undone.',
        confirmText: 'Delete',
        type: 'is-danger',
        hasIcon: true,
        ariaModal: true,
        onConfirm: () => this.deleteMaterial(data.id),
      });
    },
    onMaterialsPageChangeHandler(page) {
      this.materialsCurrentPage = page;
      this.loadMaterials();
    },

    async loadAllCategories() {
      this.categoriesTableLoading = true;
      await categories(this.categoryType)
        .then((resp) => {
          this.allCategories = resp.data.data;
          if (this.categoryType) {
            this.categories = this.allCategories
              .filter((category) => category.type === this.categoryType);
          } else {
            this.categories = this.allCategories;
          }
        })
        .catch(() => this.$buefy.toast.open({ message: 'An unexpected error occurred, please contact support@enhancify.com', type: 'is-danger', duration: 3000 }))
        .finally(() => {
          this.categoriesTableLoading = false;
        });
    },
    async loadMaterials() {
      await materials({
        categoryId: this.materialsFilterCategoryId,
        page: this.materialsCurrentPage,
        limit: this.materialsLimit,
      })
        .then((resp) => {
          this.materials = resp.data.data;
          this.materialsTotalPages = resp.data.total;
        })
        .catch(() => {
          this.materials = [];
        });
    },

    async addCategory(data) {
      this.startLoading();

      try {
        await addCategory(data);
        await this.loadAllCategories();
        this.$buefy.toast.open({ message: 'Success!', type: 'is-success', duration: 3000 });
        this.addCategoryModal.cancel();
      } catch (error) {
        if (error.response.data.invalidFields) {
          this.addCategoryModal.setInvalidFields(error.response.data.invalidFields);
        } else {
          this.$buefy.toast.open({ message: 'An unexpected error occurred, please contact support@enhancify.com', type: 'is-danger', duration: 3000 });
        }
      }

      this.finishLoading();
    },
    async editCategory(data, id) {
      this.startLoading();

      try {
        await editCategory(data, id);
        await this.loadAllCategories();
        this.$buefy.toast.open({ message: 'Success!', type: 'is-success', duration: 3000 });
        this.editCategoryModal.cancel();
      } catch (error) {
        if (error.response.data.invalidFields && !error.response.data.invalidFields.categoryId) {
          this.editCategoryModal.setInvalidFields(error.response.data.invalidFields);
        } else {
          this.$buefy.toast.open({ message: 'An unexpected error occurred, please contact support@enhancify.com', type: 'is-danger', duration: 3000 });
        }
      }

      this.finishLoading();
    },
    async deleteCategory(id) {
      this.startLoading();

      try {
        await deleteCategory(id);
        await this.loadAllCategories();
        this.materialsFilterCategoryId = null;
        await this.loadMaterials();
        this.$buefy.toast.open({ message: 'Success!', type: 'is-success', duration: 3000 });
      } catch (error) {
        this.$buefy.toast.open({
          message: 'An unexpected error occurred, please contact support@enhancify.com',
          type: 'is-danger',
          duration: 3000,
        });
      }

      this.finishLoading();
    },

    async addMaterial(data) {
      this.startLoading();

      try {
        await addMaterial(data);
        await this.loadMaterials();
        this.$buefy.toast.open({ message: 'Success!', type: 'is-success', duration: 3000 });
        this.addMaterialModal.cancel();
      } catch (error) {
        if (error.response.data.invalidFields && !error.response.data.invalidFields.categoryId) {
          this.addMaterialModal.setInvalidFields(error.response.data.invalidFields);
        } else {
          this.$buefy.toast.open({ message: 'An unexpected error occurred, please contact support@enhancify.com', type: 'is-danger', duration: 3000 });
        }
      }

      this.finishLoading();
    },
    async editMaterial(data, id) {
      this.startLoading();

      try {
        await editMaterial(data, id);
        await this.loadMaterials();
        this.$buefy.toast.open({ message: 'Success!', type: 'is-success', duration: 3000 });
        this.editMaterialModal.cancel();
      } catch (error) {
        if (error.response.data.invalidFields) {
          this.editMaterialModal.setInvalidFields(error.response.data.invalidFields);
        } else {
          this.$buefy.toast.open({
            message: 'An unexpected error occurred, please contact support@enhancify.com',
            type: 'is-danger',
            duration: 3000,
          });
        }
      }

      this.finishLoading();
    },
    async deleteMaterial(id) {
      this.startLoading();

      try {
        await deleteMaterial(id);
        await this.loadMaterials();
        this.$buefy.toast.open({ message: 'Success!', type: 'is-success', duration: 3000 });
      } catch (error) {
        this.$buefy.toast.open({
          message: 'An unexpected error occurred, please contact support@enhancify.com',
          type: 'is-danger',
          duration: 3000,
        });
      }

      this.finishLoading();
    },
  },
};
</script>

<style lang="stylus">
.bordered-table
  table
    border-bottom 1px solid rgba(143, 167, 179, 0.3) !important
</style>
<style lang="stylus" scoped>
.table-menu-categories-btn
  margin-left 20px
.cell
  @media (max-width: 768px)
    display flex
  &__label
    width 50%
    text-align start
    font-weight 600
    @media (min-width: 424px)
      width 30%
    @media (min-width: 769px)
      display none
  &__value
    width: 50%
    @media (min-width: 424px)
      width 70%
    @media (min-width: 769px)
      width 100%
   &--centered
     @media (min-width: 769px)
       text-align center
.table-menu
  display flex
  flex-direction column
  align-items: end
  justify-content: flex-end
  -webkit-justify-content flex-end
  -webkit-align-items flex-end
  @media (min-width: 500px)
    flex-direction row
    align-items: center
.ellipsis
  white-space normal
  overflow hidden
  text-overflow ellipsis
  display -webkit-box
  -webkit-line-clamp 2
  -webkit-box-orient vertical
  word-break break-word
  overflow-wrap anywhere
.bordered-table
  td:first-child
    border-left 1px solid rgba(143, 167, 179, 0.3)
  td:last-child
    border-right 1px solid rgba(143, 167, 179, 0.3)
.action-column-btn
  cursor pointer
  background transparent
  border none
  @media (min-width: 769px)
    width 100%
    padding 0
  i
    color rgba(23, 181, 73, 1)
.tabs
  display flex
  justify-content start
  border-bottom 1px solid rgba(143, 167, 179, 0.3)
  overflow visible
  .tab
    cursor pointer
    margin 0 15px
    padding-bottom: 18px
    &:first-child
      margin: 0 15px 0 0
    &.is-active
      position relative
      font-weight 600
      &:after
        content: ''
        position absolute
        bottom -1.5px
        left 0
        width 100%
        height 3px
        background rgba(23, 181, 72, 1)
</style>
